import { useRef, useState } from 'react';
import type { ConfigurationImpact } from '@laspalmas-identity/shared';
import type { IdentityTrpcClient } from '../../lib/trpc';
import { useI18n, type Locale } from '../../lib/i18n';
import { Button, Dialog, Notice } from '../../components/ui';

type ConfigurationKind = ConfigurationImpact['kind'];

type Copy = {
  affectedBindings: string;
  assignments: string;
  cancel: string;
  confirm: string;
  confirming: string;
  consents: string;
  description: string;
  loadError: string;
  loading: string;
  protectedWarning: string;
  staleWarning: string;
  storedValues: string;
  title: string;
};

const copy: Record<Locale, Copy> = {
  en: {
    affectedBindings: 'Affected OIDC bindings',
    assignments: 'Assignments',
    cancel: 'Cancel',
    confirm: 'Delete configuration',
    confirming: 'Deleting…',
    consents: 'Active consents',
    description: 'Review what will change before deleting this configuration.',
    loadError: 'We could not load the deletion impact. Try again.',
    loading: 'Loading deletion impact…',
    protectedWarning: 'This configuration is protected and cannot be deleted.',
    staleWarning: 'The realm configuration changed. Review the refreshed impact before confirming again.',
    storedValues: 'Stored values',
    title: 'Delete configuration',
  },
  gl: {
    affectedBindings: 'Vinculacións OIDC afectadas',
    assignments: 'Asignacións',
    cancel: 'Cancelar',
    confirm: 'Eliminar configuración',
    confirming: 'Eliminando…',
    consents: 'Consentimentos activos',
    description: 'Revisa o que cambiará antes de eliminar esta configuración.',
    loadError: 'Non se puido cargar o impacto da eliminación. Téntao de novo.',
    loading: 'Cargando o impacto da eliminación…',
    protectedWarning: 'Esta configuración está protexida e non se pode eliminar.',
    staleWarning: 'A configuración do reino cambiou. Revisa o impacto actualizado antes de confirmar de novo.',
    storedValues: 'Valores gardados',
    title: 'Eliminar configuración',
  },
  pl: {
    affectedBindings: 'Powiązania OIDC, których dotyczy zmiana',
    assignments: 'Przypisania',
    cancel: 'Anuluj',
    confirm: 'Usuń konfigurację',
    confirming: 'Usuwanie…',
    consents: 'Aktywne zgody',
    description: 'Sprawdź skutki przed usunięciem tej konfiguracji.',
    loadError: 'Nie udało się wczytać skutków usunięcia. Spróbuj ponownie.',
    loading: 'Wczytywanie skutków usunięcia…',
    protectedWarning: 'Ta konfiguracja jest chroniona i nie można jej usunąć.',
    staleWarning: 'Konfiguracja domeny zmieniła się. Sprawdź nowe skutki i potwierdź ponownie.',
    storedValues: 'Zapisane wartości',
    title: 'Usuń konfigurację',
  },
};

type Props = {
  client: IdentityTrpcClient;
  realmId: string;
  kind: ConfigurationKind;
  configurationId: string;
  disabled?: boolean;
  onDeleted: () => void;
};

export function ConfigurationImpactDialog({
  client,
  realmId,
  kind,
  configurationId,
  disabled = false,
  onDeleted,
}: Props) {
  const { locale, t } = useI18n();
  const text = copy[locale];
  const requestId = useRef(0);
  const [open, setOpen] = useState(false);
  const [impact, setImpact] = useState<ConfigurationImpact>();
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [stale, setStale] = useState(false);
  const [error, setError] = useState('');

  async function loadImpact(markStale = false): Promise<void> {
    const currentRequest = ++requestId.current;
    setImpact(undefined);
    setLoading(true);
    setError('');
    setStale(markStale);

    try {
      const result = await client.admin.configurationImpact.query({
        realmId,
        kind,
        id: configurationId,
      });
      if (requestId.current === currentRequest) setImpact(result);
    } catch {
      if (requestId.current === currentRequest) setError(text.loadError);
    } finally {
      if (requestId.current === currentRequest) setLoading(false);
    }
  }

  function openDialog(): void {
    setOpen(true);
    void loadImpact();
  }

  function closeDialog(): void {
    if (deleting) return;
    requestId.current += 1;
    setOpen(false);
    setImpact(undefined);
    setError('');
    setStale(false);
  }

  async function confirmDeletion(): Promise<void> {
    if (!impact || impact.protected || deleting) return;
    setDeleting(true);
    setError('');

    try {
      await client.admin.removeConfiguration.mutate({
        kind,
        id: configurationId,
        realmId,
        expectedConfigurationVersion: impact.configurationVersion,
      });
      setOpen(false);
      setImpact(undefined);
      setStale(false);
      onDeleted();
    } catch (cause) {
      if (domainErrorCode(cause) === 'CONFIGURATION_CONFLICT') {
        await loadImpact(true);
      } else {
        setError(t('adminOperationFailed'));
      }
    } finally {
      setDeleting(false);
    }
  }

  const title = impact ? `${text.title}: ${impact.name}` : text.title;

  return (
    <>
      <Button
        size="small"
        variant="danger"
        disabled={disabled}
        onClick={openDialog}
      >
        {t('adminRemove')}
      </Button>
      <Dialog
        open={open}
        title={title}
        description={text.description}
        onClose={closeDialog}
      >
        {loading && (
          <p className="loading-inline" role="status">
            <span className="spinner" /> {text.loading}
          </p>
        )}
        {stale && !loading && (
          <Notice tone="warning">{text.staleWarning}</Notice>
        )}
        {error && <Notice tone="danger">{error}</Notice>}
        {impact && (
          <ImpactDetails
            impact={impact}
            protectedWarning={text.protectedWarning}
            labels={text}
          />
        )}
        <div className="dialog-actions">
          <Button
            type="button"
            variant="ghost"
            disabled={deleting}
            onClick={closeDialog}
          >
            {text.cancel}
          </Button>
          <Button
            type="button"
            variant="danger"
            disabled={!impact || impact.protected || loading || deleting}
            onClick={() => void confirmDeletion()}
          >
            {deleting ? text.confirming : text.confirm}
          </Button>
        </div>
      </Dialog>
    </>
  );
}

function ImpactDetails({
  impact,
  protectedWarning,
  labels,
}: {
  impact: ConfigurationImpact;
  protectedWarning: string;
  labels: Copy;
}) {
  return (
    <div>
      {impact.protected && (
        <Notice tone="danger">{protectedWarning}</Notice>
      )}
      <dl>
        <ImpactCount label={labels.assignments} value={impact.assignmentCount} />
        <ImpactCount label={labels.storedValues} value={impact.valueCount} />
        <ImpactCount label={labels.consents} value={impact.consentCount} />
        <ImpactCount label={labels.affectedBindings} value={impact.affectedBindings.length} />
      </dl>
      {impact.affectedBindings.length > 0 && (
        <ul>
          {impact.affectedBindings.map((binding) => (
            <li key={binding.bindingId}>
              <strong>{binding.applicationName}</strong>{' '}
              <code>{binding.clientId}</code>
            </li>
          ))}
        </ul>
      )}
      {impact.warnings.map((warning) => (
        <Notice key={warning.code} tone="warning">
          {warning.message}
        </Notice>
      ))}
    </div>
  );
}

function ImpactCount({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function domainErrorCode(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const value = error as {
    message?: unknown;
    code?: unknown;
    data?: { domain?: { code?: unknown } };
  };
  if (typeof value.data?.domain?.code === 'string') return value.data.domain.code;
  if (typeof value.code === 'string') return value.code;
  return typeof value.message === 'string' ? value.message : undefined;
}
